import { MDXProvider } from "@mdx-js/react";
import { useLocation, useSearch } from "@tanstack/react-router";
import stringApplySearch from "common/utils/stringApplySearch";
import { useMemo, useEffect, useState, useDeferredValue, type ReactNode } from "react";
import { Helmet as Head } from "react-helmet-async";
import LineCapsule from "ui/components/LineCapsule";
import Link from "ui/components/Link";
import ProductName from "ui/components/ProductName";
import cx from "ui/cx";
import IconUpArrow from "ui/icons/IconUpArrow";
import Accordion from "ui/layouts/Accordion";
import Header from "ui/layouts/Header";
import { HorizontalSplitGroup, SplitAlignment } from "ui/layouts/Split";
import useMDXComponents from "ui/mdx/MdxComponents";
import SearchInput from "ui/search/SearchInput";
import styles from "./ComponentLibraryLayout.module.scss";
import { loadDynamicComponents, staticRegistry, type ComponentRegistryEntry } from "./componentRegistry";

const URL_BASE = "/admin/component-library";

interface NavigationItem {
  page: string;
  name: string;
  group?: string;
}

export interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

function SidebarLink({ page, pageName }: { page: string; pageName: ReactNode }) {
  const location = useLocation();
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  const href = page === "" ? URL_BASE : `${URL_BASE}?id=${encodeURIComponent(page)}`;
  const isActive = page === "" ? location.pathname === URL_BASE && !searchParams.id : searchParams.id === page;

  return (
    <Link href={href} className={cx(styles.link, isActive && styles.active)}>
      {pageName}
    </Link>
  );
}

export default function ComponentLibraryLayout({
  children,
  headerText,
  subheaderText,
  externalLinks,
}: {
  children: ReactNode;
  headerText: string;
  subheaderText: string | ReactNode;
  externalLinks?: { label: string; url: string }[];
}) {
  const [runtimeRegistry, setRuntimeRegistry] = useState<Record<string, ComponentRegistryEntry>>({ ...staticRegistry });
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  useEffect(() => {
    // Load dynamic components at runtime
    const loadComponents = (): void => {
      try {
        const dynamicComponents = loadDynamicComponents();
        setRuntimeRegistry({
          ...staticRegistry,
          ...dynamicComponents,
        });
      } catch (error) {
        console.warn("Failed to load dynamic components for navigation:", error);
      }
    };

    loadComponents();
  }, []);

  const sections = useMemo(() => {
    const sections: Record<string, NavigationSection> = {};
    for (const [key, value] of Object.entries(runtimeRegistry)) {
      // Filter by search term
      if (deferredSearch && !stringApplySearch(value.name, deferredSearch)) {
        continue;
      }
      if (!sections[value.section]) {
        sections[value.section] = {
          title: value.section,
          items: [],
        };
      }
      sections[value.section].items.push({ page: key, name: value.name, group: value.group });
    }
    return sections;
  }, [runtimeRegistry, deferredSearch]);

  /** Group items by their group field (for Components section) */
  const groupItemsByGroup = (items: NavigationItem[]): Record<string, NavigationItem[]> => {
    return items.reduce<Record<string, NavigationItem[]>>((acc, item) => {
      const group = item.group ?? "other";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});
  };

  const startHereSection = sections["Start Here"];
  const designTokensSection = sections["Design Tokens"];
  const componentsSection = sections["Components"];
  const componentGroups = componentsSection ? groupItemsByGroup(componentsSection.items) : {};

  const sideNav = (
    <nav className={styles.sidebar}>
      <div className={styles.navigation}>
        <section className={styles.section}>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            clearSearch={() => setSearch("")}
            placeholder="Search components"
            className={styles.searchInput}
            inputSize="mediumLarge"
          />
        </section>
        {/* Start Here - flat section */}
        {startHereSection && (
          <section className={styles.section}>
            {startHereSection.items
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => (
                <SidebarLink key={item.page} page={item.page} pageName={item.name} />
              ))}
          </section>
        )}

        <Accordion.Root type="multiple" defaultValue={["design-system"]} fullWidth className={styles.accordion}>
          {/* Design System */}
          {designTokensSection && designTokensSection.items.length > 0 && (
            <Accordion.Item value="design-system">
              <Accordion.Header>
                <Accordion.Trigger className={styles.accordionTrigger}>{designTokensSection.title}</Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className={styles.section}>
                {designTokensSection.items
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item) => (
                    <SidebarLink key={item.page} page={item.page} pageName={item.name} />
                  ))}
              </Accordion.Content>
            </Accordion.Item>
          )}

          {/* Components grouped by directory */}
          {Object.entries(componentGroups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([group, items]) => (
              <Accordion.Item key={group} value={group}>
                <Accordion.Header>
                  <Accordion.Trigger className={styles.accordionTrigger}>{group}</Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className={styles.section}>
                  {items
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((item) => (
                      <SidebarLink key={item.page} page={item.page} pageName={item.name} />
                    ))}
                </Accordion.Content>
              </Accordion.Item>
            ))}
        </Accordion.Root>
      </div>
    </nav>
  );

  const mainContent = (
    <main className={styles.content}>
      <header className={styles.header}>
        <h1>{headerText}</h1>
        {subheaderText ||
          (externalLinks && (
            <div className={styles.subtext}>
              {subheaderText && <p>{subheaderText}</p>}
              {externalLinks && externalLinks.length > 0 && (
                <div className={styles.externalLinks}>
                  <label htmlFor="">External links</label>
                  {externalLinks.map(({ label, url }) => (
                    <Link key={url} href={url} target="_blank">
                      <LineCapsule label={label} icon={<IconUpArrow />} />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
      </header>
      <MDXProvider components={useMDXComponents()}>
        <div className={styles.contentWrapper}>{children}</div>
      </MDXProvider>
    </main>
  );

  return (
    <div className={styles.wrapper}>
      <Head>
        <title>{headerText} | Higharc Components</title>
      </Head>
      <Header
        className={styles.topNav}
        left={
          <Link href="/admin">
            <ProductName productName="Higharc Components" title="Higharc Components" />
          </Link>
        }
      />
      <div className={styles.horizontalWrapper}>
        <HorizontalSplitGroup
          initialFixedSideSize={264}
          flexibleSide={SplitAlignment.LAST}
          minPanelSize={200}
          highlightSeparator
        >
          {sideNav}
          {mainContent}
        </HorizontalSplitGroup>
      </div>
    </div>
  );
}
